import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Send, Building2, Calendar, Users, Megaphone, Clock, CheckCircle2,
  XCircle, ChevronDown, ChevronUp, MapPin, Phone, Mail, FileText, Briefcase, ArrowLeft,
  Share2, ImageIcon, Palette
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const requestTypes = [
  {
    id: 'business_visit',
    name: 'Business Visit Request',
    description: 'Request a formal visit from the chamber leadership to your business.',
    icon: Building2,
    color: 'bg-blue-500',
    fields: [
      { name: 'business_name', label: 'Business Name', type: 'text', required: true },
      { name: 'business_address', label: 'Business Address', type: 'text', required: true },
      { name: 'preferred_date', label: 'Preferred Visit Date', type: 'date', required: true },
      { name: 'preferred_time', label: 'Preferred Time', type: 'select', options: ['Morning (9AM-12PM)', 'Afternoon (12PM-3PM)', 'Evening (3PM-6PM)'], required: true },
      { name: 'contact_name', label: 'Contact Person', type: 'text', required: true },
      { name: 'contact_phone', label: 'Contact Phone', type: 'tel', required: true },
      { name: 'contact_email', label: 'Contact Email', type: 'email', required: true },
      { name: 'visit_purpose', label: 'Purpose of Visit', type: 'textarea', required: true, placeholder: 'Describe why you are requesting a business visit...' },
      { name: 'special_notes', label: 'Special Notes', type: 'textarea', required: false, placeholder: 'Any special instructions or notes...' },
    ],
  },
  {
    id: 'member_event',
    name: 'Member Event Submission',
    description: 'Submit your own event to be featured on the chamber calendar.',
    icon: Calendar,
    color: 'bg-green-500',
    fields: [
      { name: 'event_title', label: 'Event Title', type: 'text', required: true },
      { name: 'event_description', label: 'Event Description', type: 'textarea', required: true, placeholder: 'Describe the event in detail...' },
      { name: 'event_date', label: 'Event Date', type: 'date', required: true },
      { name: 'event_time', label: 'Event Time', type: 'text', required: true, placeholder: 'e.g., 6:00 PM - 9:00 PM' },
      { name: 'event_location', label: 'Event Location', type: 'text', required: true },
      { name: 'expected_attendance', label: 'Expected Attendance', type: 'number', required: false },
      { name: 'event_category', label: 'Event Category', type: 'select', options: ['Networking', 'Workshop', 'Gala', 'Social', 'Community', 'Business', 'Other'], required: true },
      { name: 'registration_link', label: 'Registration Link (if applicable)', type: 'url', required: false },
      { name: 'additional_info', label: 'Additional Information', type: 'textarea', required: false, placeholder: 'Any additional details...' },
    ],
  },
  {
    id: 'appointment',
    name: 'Request Appointment',
    description: 'Schedule a one-on-one meeting with the chamber leadership or team.',
    icon: Users,
    color: 'bg-purple-500',
    fields: [
      { name: 'meeting_with', label: 'Request Meeting With', type: 'select', options: ['Chamber President', 'Membership Team', 'Marketing Team', 'Events Team', 'Board Director', 'Other'], required: true },
      { name: 'preferred_date', label: 'Preferred Date', type: 'date', required: true },
      { name: 'preferred_time', label: 'Preferred Time', type: 'select', options: ['Morning (9AM-12PM)', 'Afternoon (12PM-3PM)', 'Evening (3PM-6PM)'], required: true },
      { name: 'meeting_format', label: 'Meeting Format', type: 'select', options: ['In-Person', 'Virtual (Zoom)', 'Phone Call'], required: true },
      { name: 'topic', label: 'Meeting Topic', type: 'text', required: true, placeholder: 'Brief subject of the meeting' },
      { name: 'details', label: 'Meeting Details', type: 'textarea', required: true, placeholder: 'Describe what you would like to discuss...' },
      { name: 'contact_phone', label: 'Your Phone Number', type: 'tel', required: true },
    ],
  },
  {
    id: 'promotion',
    name: 'Request Promotion',
    description: 'Request promotion of your business through chamber channels.',
    icon: Megaphone,
    color: 'bg-orange-500',
    fields: [
      { name: 'business_name', label: 'Business Name', type: 'text', required: true },
      { name: 'promotion_type', label: 'Promotion Type', type: 'select', options: ['Social Media Post', 'Newsletter Feature', 'Website Spotlight', 'Event Sponsorship', 'Multiple Channels'], required: true },
      { name: 'promotion_details', label: 'Promotion Details', type: 'textarea', required: true, placeholder: 'Describe what you want to promote and any key messages...' },
      { name: 'target_audience', label: 'Target Audience', type: 'text', required: false, placeholder: 'Who should this promotion reach?' },
      { name: 'preferred_date', label: 'Preferred Promotion Date', type: 'date', required: false },
      { name: 'budget', label: 'Promotion Budget (if applicable)', type: 'text', required: false, placeholder: 'e.g., $500' },
      { name: 'website', label: 'Business Website', type: 'url', required: false },
      { name: 'special_offer', label: 'Special Offer or Discount (optional)', type: 'text', required: false, placeholder: 'e.g., 20% off for chamber members' },
    ],
  },
  {
    id: 'social_media_post',
    name: 'Social Media Post Request',
    description: 'Request a social media post to promote your business or event.',
    icon: Share2,
    color: 'bg-pink-500',
    fields: [
      { name: 'business_name', label: 'Business Name', type: 'text', required: true },
      { name: 'campaign_topic', label: 'Campaign Topic / Purpose', type: 'text', required: true, placeholder: 'e.g., Grand Opening, New Service, Sale' },
      { name: 'goal', label: 'Goal of This Post', type: 'select', options: ['Brand Awareness', 'Event Promotion', 'Product/Service Launch', 'Sale/Discount', 'Community Engagement', 'Hiring/Recruitment', 'Other'], required: true },
      { name: 'caption_direction', label: 'Caption Direction / Key Message', type: 'textarea', required: true, placeholder: 'What should the post say? What tone?' },
      { name: 'cta', label: 'Call-to-Action (CTA)', type: 'text', required: true, placeholder: 'e.g., Visit our website, Call now, Book today' },
      { name: 'deadline', label: 'Post Deadline', type: 'date', required: true },
      { name: 'notes', label: 'Additional Notes', type: 'textarea', required: false, placeholder: 'Hashtags, mentions, brand guidelines...' },
    ],
  },
  {
    id: 'flyer_promotion',
    name: 'Flyer / Promotion Request',
    description: 'Request a flyer or promotional material for your event or campaign.',
    icon: Palette,
    color: 'bg-indigo-500',
    fields: [
      { name: 'promo_title', label: 'Event or Promo Title', type: 'text', required: true },
      { name: 'purpose', label: 'Purpose of the Flyer', type: 'textarea', required: true, placeholder: 'What is this flyer for? What should it communicate?' },
      { name: 'preferred_colors', label: 'Preferred Colors / Theme', type: 'text', required: false, placeholder: 'e.g., Red and gold, corporate blue' },
      { name: 'references', label: 'Design References / Inspiration', type: 'textarea', required: false, placeholder: 'Links or descriptions of designs you like' },
      { name: 'event_date', label: 'Event / Promo Date', type: 'date', required: true },
      { name: 'location', label: 'Location (if applicable)', type: 'text', required: false },
      { name: 'cta', label: 'Call-to-Action', type: 'text', required: true, placeholder: 'e.g., Register now, Join us, Call for info' },
      { name: 'special_notes', label: 'Special Notes / Requirements', type: 'textarea', required: false, placeholder: 'Sizing, format, text that must be included...' },
      { name: 'deadline', label: 'Delivery Deadline', type: 'date', required: true },
    ],
  },
  {
    id: 'event_promotion',
    name: 'Event Promotion Request',
    description: 'Request promotional support and materials for your upcoming event.',
    icon: Calendar,
    color: 'bg-rose-500',
    fields: [
      { name: 'event_name', label: 'Event Name', type: 'text', required: true },
      { name: 'event_date', label: 'Event Date', type: 'date', required: true },
      { name: 'event_time', label: 'Event Time', type: 'text', required: true, placeholder: 'e.g., 6:00 PM - 9:00 PM' },
      { name: 'event_location', label: 'Event Location', type: 'text', required: true },
      { name: 'event_description', label: 'Event Description', type: 'textarea', required: true, placeholder: 'Describe your event, what attendees can expect...' },
      { name: 'registration_link', label: 'Registration / RSVP Link', type: 'url', required: false },
      { name: 'target_audience', label: 'Target Audience', type: 'text', required: true, placeholder: 'Who should attend? e.g., Business owners, entrepreneurs' },
      { name: 'deliverables_needed', label: 'What Do You Need?', type: 'select', options: ['Social Media Posts Only', 'Flyer Only', 'Social Media + Flyer', 'Full Campaign (Social + Flyer + Newsletter)', 'Other'], required: true },
      { name: 'flyer_references', label: 'Flyer Design References', type: 'textarea', required: false, placeholder: 'Links or descriptions of flyer styles you like' },
      { name: 'special_notes', label: 'Special Notes', type: 'textarea', required: false, placeholder: 'Anything else the marketing team should know...' },
    ],
  },
];

const statusConfig = {
  pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-700', label: 'Pending' },
  approved: { icon: CheckCircle2, color: 'bg-green-100 text-green-700', label: 'Approved' },
  declined: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'Declined' },
  in_progress: { icon: Clock, color: 'bg-blue-100 text-blue-700', label: 'In Progress' },
};

const PortalRequestCenter = () => {
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('types');
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [expandedRequest, setExpandedRequest] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API}/portal/requests`, { withCredentials: true });
      setMyRequests(res.data);
    } catch (err) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedType) return;

    const type = requestTypes.find(t => t.id === selectedType);
    const missingFields = type.fields.filter(f => f.required && !formData[f.name]);
    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/portal/requests`, {
        request_type: selectedType,
        data: formData,
      }, { withCredentials: true });
      toast.success('Request submitted successfully!');
      setFormData({});
      setSelectedType(null);
      setActiveView('history');
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const commonClass = "w-full border border-slate-200 rounded-sm p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]";

    if (field.type === 'textarea') {
      return (
        <textarea
          value={formData[field.name] || ''}
          onChange={e => setFormData({ ...formData, [field.name]: e.target.value })}
          placeholder={field.placeholder || ''}
          className={`${commonClass} min-h-[100px]`}
          data-testid={`field-${field.name}`}
        />
      );
    }
    if (field.type === 'select') {
      return (
        <select
          value={formData[field.name] || ''}
          onChange={e => setFormData({ ...formData, [field.name]: e.target.value })}
          className={commonClass}
          data-testid={`field-${field.name}`}
        >
          <option value="">Select {field.label}</option>
          {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    }
    return (
      <input
        type={field.type}
        value={formData[field.name] || ''}
        onChange={e => setFormData({ ...formData, [field.name]: e.target.value })}
        placeholder={field.placeholder || ''}
        className={commonClass}
        data-testid={`field-${field.name}`}
      />
    );
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="portal-request-center">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-green-500 rounded-sm flex items-center justify-center">
            <Send className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#002249]">Request Center</h1>
        </div>
        <p className="text-slate-600 mt-2">Submit requests, schedule appointments, and promote your business through the chamber.</p>
      </motion.div>

      {/* View Toggle */}
      <div className="flex space-x-2 mb-6">
        <button
          onClick={() => { setActiveView('types'); setSelectedType(null); }}
          className={`px-4 py-2 rounded-sm text-sm font-medium transition-all ${activeView === 'types' ? 'bg-[#002249] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
          data-testid="view-new-request"
        >
          New Request
        </button>
        <button
          onClick={() => setActiveView('history')}
          className={`px-4 py-2 rounded-sm text-sm font-medium transition-all ${activeView === 'history' ? 'bg-[#002249] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
          data-testid="view-request-history"
        >
          My Requests ({myRequests.length})
        </button>
      </div>

      {activeView === 'types' && !selectedType && (
        <div className="grid md:grid-cols-2 gap-4">
          {requestTypes.map((type, index) => (
            <motion.div
              key={type.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * index }}
              onClick={() => { setSelectedType(type.id); setFormData({}); }}
              className="bg-white border border-slate-200 rounded-sm p-5 hover:shadow-md hover:border-[#002249]/30 transition-all cursor-pointer"
              data-testid={`request-type-${type.id}`}
            >
              <div className="flex items-start space-x-4">
                <div className={`w-12 h-12 ${type.color} rounded-sm flex items-center justify-center flex-shrink-0`}>
                  <type.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#002249] mb-1">{type.name}</h3>
                  <p className="text-sm text-slate-600">{type.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Request Form */}
      {activeView === 'types' && selectedType && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <button
            onClick={() => { setSelectedType(null); setFormData({}); }}
            className="flex items-center text-sm text-slate-600 hover:text-[#002249] mb-4"
            data-testid="back-to-types-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to request types
          </button>

          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="p-5 border-b border-slate-200">
              <h2 className="text-lg font-bold text-[#002249]">
                {requestTypes.find(t => t.id === selectedType)?.name}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {requestTypes.find(t => t.id === selectedType)?.description}
              </p>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4" data-testid="request-form">
              {requestTypes.find(t => t.id === selectedType)?.fields.map(field => (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {field.label}
                    {field.required && <span className="text-[#E63946] ml-1">*</span>}
                  </label>
                  {renderField(field)}
                </div>
              ))}
              <div className="pt-4 flex space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-sm"
                  onClick={() => { setSelectedType(null); setFormData({}); }}
                  data-testid="cancel-request-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#002249] hover:bg-[#001630] rounded-sm"
                  data-testid="submit-request-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </div>
            </form>
          </div>
        </motion.div>
      )}

      {/* Request History */}
      {activeView === 'history' && (
        <div className="space-y-3" data-testid="request-history">
          {myRequests.length > 0 ? myRequests.map((req, index) => {
            const typeInfo = requestTypes.find(t => t.id === req.request_type);
            const status = statusConfig[req.status] || statusConfig.pending;
            const isExpanded = expandedRequest === req.id;

            return (
              <motion.div
                key={req.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.03 * index }}
                className="bg-white border border-slate-200 rounded-sm overflow-hidden"
                data-testid={`request-item-${index}`}
              >
                <div
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                  onClick={() => setExpandedRequest(isExpanded ? null : req.id)}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 ${typeInfo?.color || 'bg-slate-500'} rounded-sm flex items-center justify-center`}>
                      {typeInfo?.icon ? <typeInfo.icon className="w-5 h-5 text-white" /> : <Send className="w-5 h-5 text-white" />}
                    </div>
                    <div>
                      <h4 className="font-medium text-[#002249] text-sm">{typeInfo?.name || req.request_type}</h4>
                      <p className="text-xs text-slate-500">{new Date(req.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-sm ${status.color}`}>
                      {status.label}
                    </span>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </div>
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-slate-200 overflow-hidden"
                    >
                      <div className="p-4 bg-slate-50 space-y-2">
                        {req.data && Object.entries(req.data).map(([key, value]) => (
                          <div key={key} className="flex flex-col sm:flex-row sm:items-center text-sm">
                            <span className="font-medium text-slate-600 capitalize min-w-[160px]">
                              {key.replace(/_/g, ' ')}:
                            </span>
                            <span className="text-slate-700">{value || '—'}</span>
                          </div>
                        ))}
                        {req.admin_notes && (
                          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-sm text-sm">
                            <p className="font-medium text-blue-700 mb-1">Admin Notes:</p>
                            <p className="text-blue-600">{req.admin_notes}</p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          }) : (
            <div className="text-center py-16 text-slate-500">
              <Send className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium">No requests yet</p>
              <p className="text-sm mt-1">Submit a request to get started.</p>
              <Button className="mt-4 bg-[#002249] hover:bg-[#001630] rounded-sm" onClick={() => setActiveView('types')} data-testid="go-to-new-request">
                New Request
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PortalRequestCenter;
